import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../components/Toast";
import LoadingSpinner from "../components/LoadingSpinner";
import ConfirmDialog from "../components/ConfirmDialog";
import WidgetPreview from "../components/WidgetPreview";
import BasicInfoStep from "../components/WidgetFormSteps/BasicInfoStep";
import BrandingStep from "../components/WidgetFormSteps/BrandingStep";
import AIBehaviorStep from "../components/WidgetFormSteps/AIBehaviorStep";
import AdvancedStep from "../components/WidgetFormSteps/AdvancedStep";
import useConfirm from "../hooks/useConfirm";
import useWidgetFormDraft from "../hooks/useWidgetFormDraft";
import useUserPlan from "../hooks/useUserPlan";
import { widgetApi } from "../services/api";
import errorTracker from "../utils/errorTracker";
import { VALIDATION_MESSAGES } from "../utils/constants";
import useCredits from "../hooks/useCredits";
import { getErrorMessage, isValidHexColor } from "../utils/formatters";
import { validateStep } from "../utils/widgetForm/validation";
import {
  STEPS,
  DEFAULT_FORM_DATA,
  mapWidgetToFormData,
  prepareSubmitPayload,
} from "../utils/widgetForm/formDefaults";
import "./WidgetForm.css";

const WidgetForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirmState, confirm } = useConfirm();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [stepsWithErrors, setStepsWithErrors] = useState(new Set());
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [models, setModels] = useState([]);
  const [creditPrices, setCreditPrices] = useState({});
  const [creditPricesAvailable, setCreditPricesAvailable] = useState(true);
  const defaultModelRef = useRef("");

  // Fetch credit balances for display in AI Behavior step
  const {
    credits,
    loading: creditsLoading,
    error: creditsError,
  } = useCredits();

  // Fetch and cache user's plan to determine feature availability
  const {
    plan: userPlan,
    loading: planLoading,
    error: planFetchError,
  } = useUserPlan();

  // Show toast notification if plan fetch fails.
  // toast is stable (useMemo in ToastProvider) so it won't cause re-fires,
  // but the exhaustive-deps lint rule requires it in the array.
  useEffect(() => {
    if (planFetchError) {
      toast.warning(planFetchError);
    }
  }, [planFetchError, toast]);

  // Use draft management hook
  const { clearDraft } = useWidgetFormDraft(
    id,
    formData,
    setFormData,
    setIsFormDirty,
    toast,
  );

  // Load existing widget if editing
  const loadWidget = useCallback(async () => {
    setLoading(true);
    try {
      const response = await widgetApi.getWidget(id);
      setFormData(mapWidgetToFormData(response.widget));
    } catch (err) {
      errorTracker.captureException(err, {
        context: "loadWidget",
        widgetId: id,
      });
      toast.error(getErrorMessage(err, "Failed to load widget"));
      navigate("/widgets");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => {
    if (id) {
      loadWidget();
    }
  }, [id, loadWidget]);

  // Fetch widget form config (allowed models) from backend
  useEffect(() => {
    const controller = new AbortController();
    widgetApi
      .getConfig({ signal: controller.signal })
      .then((config) => {
        setModels(config.models);
        setCreditPrices(config.creditPrices || {});
        setCreditPricesAvailable(config.creditPricesAvailable !== false);
        // Stash the backend default so we can re-apply it after draft clear
        if (config.defaultModel) {
          defaultModelRef.current = config.defaultModel;
        }
        // Update default model in form if not editing an existing widget
        if (!id && config.defaultModel) {
          setFormData((prev) => ({ ...prev, model: config.defaultModel }));
        }
      })
      .catch((err) => {
        if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
        // Non-fatal: form still works, dropdown will just be empty
        toast.error(getErrorMessage(err, "Failed to load model options"));
      });
    return () => controller.abort();
  }, [id, toast]);

  // Add beforeunload protection for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty && !saving) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isFormDirty, saving]);

  const handleInputChange = (field, value) => {
    // Validate hex colors — always update formData so typing isn't blocked,
    // but set/clear the error for inline feedback.
    // NOTE: # prefix is handled by ColorPicker.handleInputChange — not duplicated here.
    if (field === "primary_color" || field === "secondary_color") {
      if (value && !isValidHexColor(value)) {
        setErrors((prev) => ({
          ...prev,
          [field]: VALIDATION_MESSAGES.COLOR_INVALID,
        }));
      } else if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    } else if (errors[field]) {
      // Clear error for non-color fields when value changes
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setIsFormDirty(true);
  };

  const handleValidateStep = (step) => {
    const newErrors = validateStep(step, formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setStepsWithErrors((prev) => new Set(prev).add(step));
    } else {
      setStepsWithErrors((prev) => {
        const next = new Set(prev);
        next.delete(step);
        return next;
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (handleValidateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleClearDraft = async () => {
    const confirmed = await confirm({
      title: "Clear Draft",
      message: VALIDATION_MESSAGES.CLEAR_DRAFT,
      confirmText: "Clear",
      cancelText: "Cancel",
      variant: "warning",
    });

    if (confirmed) {
      clearDraft();
      setFormData({ ...DEFAULT_FORM_DATA, model: defaultModelRef.current });
      setIsFormDirty(false);
      toast.info("Draft cleared");
    }
  };

  const handleCancel = async () => {
    if (isFormDirty) {
      const confirmed = await confirm({
        title: "Discard Changes",
        message:
          "You have unsaved changes. Are you sure you want to leave? Your changes will be lost.",
        confirmText: "Discard",
        cancelText: "Keep Editing",
        variant: "warning",
      });

      if (!confirmed) {
        return;
      }
    }

    navigate("/widgets");
  };

  const handleSubmit = async () => {
    // Validate ALL steps before submitting, not just the current one
    let hasErrors = false;
    const allStepErrors = new Set();

    for (const step of STEPS) {
      const stepErrors = validateStep(step.id, formData);
      if (Object.keys(stepErrors).length > 0) {
        hasErrors = true;
        allStepErrors.add(step.id);
      }
    }

    if (hasErrors) {
      setStepsWithErrors(allStepErrors);
      // Navigate to the first step with errors
      const firstErrorStep = Math.min(...allStepErrors);
      if (firstErrorStep !== currentStep) {
        setCurrentStep(firstErrorStep);
      }
      // Run validation on that step to populate field-level errors
      handleValidateStep(firstErrorStep);
      toast.error(
        "Please fix errors in the highlighted steps before submitting.",
      );
      return;
    }

    setSaving(true);
    try {
      const widgetData = prepareSubmitPayload(formData);

      if (id) {
        await widgetApi.updateWidget(id, widgetData);
        toast.success("Widget updated successfully");
      } else {
        await widgetApi.createWidget(widgetData);
        toast.success("Widget created successfully");
        clearDraft();
      }

      setIsFormDirty(false);
      navigate("/widgets");
    } catch (err) {
      errorTracker.captureException(err, {
        context: "saveWidget",
        widgetId: id,
      });
      toast.error(getErrorMessage(err, "Failed to save widget"));
    } finally {
      setSaving(false);
    }
  };

  const renderCurrentStep = () => {
    const stepProps = { formData, errors, onInputChange: handleInputChange };

    switch (currentStep) {
      case 1:
        return <BasicInfoStep {...stepProps} />;
      case 2:
        return (
          <BrandingStep
            {...stepProps}
            userPlan={userPlan}
            planFetchError={planFetchError}
            planLoading={planLoading}
          />
        );
      case 3:
        return (
          <AIBehaviorStep
            {...stepProps}
            models={models}
            creditPrices={creditPrices}
            creditPricesAvailable={creditPricesAvailable}
            credits={credits}
            creditsLoading={creditsLoading}
            creditsError={creditsError}
          />
        );
      case 4:
        return <AdvancedStep {...stepProps} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="widget-form-container">
        <LoadingSpinner centered text="Loading widget data..." />
      </div>
    );
  }

  return (
    <div className="widget-form-container">
      <div className="widget-form-header">
        <div>
          <h1>{id ? "Edit Widget" : "Create New Widget"}</h1>
          <p className="widget-form-subtitle">
            {STEPS[currentStep - 1].description}
          </p>
        </div>
        {!id && isFormDirty && (
          <button
            type="button"
            className="widget-form-btn widget-form-btn-text"
            onClick={handleClearDraft}
            disabled={saving}
          >
            Clear Draft
          </button>
        )}
      </div>

      {/* Stepper */}
      <div className="widget-form-stepper">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={`widget-form-step ${
              currentStep === step.id ? "active" : ""
            } ${currentStep > step.id ? "completed" : ""} ${
              stepsWithErrors.has(step.id) ? "has-error" : ""
            }`}
            title={
              stepsWithErrors.has(step.id)
                ? "This step has validation errors"
                : ""
            }
          >
            <div className="widget-form-step-number">
              {stepsWithErrors.has(step.id)
                ? "!"
                : currentStep > step.id
                  ? "✓"
                  : step.id}
            </div>
            <div className="widget-form-step-label">{step.title}</div>
            {index < STEPS.length - 1 && (
              <div className="widget-form-step-line" />
            )}
          </div>
        ))}
      </div>

      {/* Form Content with Preview */}
      <div className="widget-form-layout">
        <div className="widget-form-content">{renderCurrentStep()}</div>

        {/* Live Preview - Show on branding step */}
        {currentStep === 2 && (
          <div className="widget-form-preview">
            <WidgetPreview
              widget={{
                display_name:
                  formData.display_name || formData.name || "Chat Assistant",
                welcome_message: formData.welcome_message,
                branding: {
                  primary_color: formData.primary_color,
                  avatar_url: formData.avatar_url,
                  position: "bottom-right",
                  input_placeholder: formData.placeholder_text,
                  show_branding: formData.show_branding !== false,
                },
              }}
            />
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="widget-form-actions">
        <button
          type="button"
          onClick={handleCancel}
          className="widget-form-btn widget-form-btn-secondary"
          disabled={saving}
        >
          Cancel
        </button>

        <div className="widget-form-actions-right">
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevious}
              className="widget-form-btn widget-form-btn-secondary"
              disabled={saving}
            >
              Previous
            </button>
          )}

          {currentStep < STEPS.length ? (
            <button
              type="button"
              onClick={handleNext}
              className="widget-form-btn widget-form-btn-primary"
              disabled={saving}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              className="widget-form-btn widget-form-btn-primary"
              disabled={saving}
            >
              {saving ? "Saving..." : id ? "Update Widget" : "Create Widget"}
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog {...confirmState} />
    </div>
  );
};

export default WidgetForm;
